import { logger } from '@/utils/logger/logger';
import { type CustomLogger } from '@/utils/logger/types';

type Enrich = (payload: unknown) => unknown;

const wrapMethod = (
  method: CustomLogger['info'],
  enrich: Enrich
): CustomLogger['info'] => {
  return (...args: unknown[]) => method(enrich(args[0]), ...args.slice(1));
};

/**
 * Wrap a `CustomLogger` so every method auto-merges the enrichment and
 * `child()` returns a wrapped child too — without the recursion any
 * `enrichedLogger.child({...})` call would silently bypass enrichment.
 */
const wrapLogger = (base: CustomLogger, enrich: Enrich): CustomLogger => ({
  trace: wrapMethod(base.trace, enrich),
  debug: wrapMethod(base.debug, enrich),
  info: wrapMethod(base.info, enrich),
  warn: wrapMethod(base.warn, enrich),
  error: wrapMethod(base.error, enrich),
  fatal: wrapMethod(base.fatal, enrich),
  log: wrapMethod(base.log, enrich),
  child: (bindings) => wrapLogger(base.child(bindings), enrich),
});

/**
 * Build a module-scoped logger (`logger.child({ module })`) whose every method
 * passes the first payload argument through `enrich` before logging. Shared
 * decorator infrastructure for `dictationLogger` (correlation ids) and
 * `chronicleLogger` (device-state snapshot) — each supplies only its `enrich`.
 *
 * `enrich` runs per log call, so it can read live state; it must never throw
 * (wrap risky reads in try/catch) and should return the payload unchanged when
 * there is nothing to add.
 *
 * `logger.child` always exists in production; the guards only tolerate partial
 * test mocks of the base logger (no `child`, or a bare jest.fn() `child` that
 * returns undefined), falling back to the mock as-is so module init never throws.
 */
export function createEnrichedLogger(module: string, enrich: Enrich): CustomLogger {
  const baseLogger =
    (typeof logger.child === 'function' ? logger.child({ module }) : undefined) ?? logger;
  return wrapLogger(baseLogger, enrich);
}
